import { Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { MANAGEMENT_EMAIL, SOCIALS } from "../../data/velvet";
import { MaskLine, FadeUp, Overline } from "./Reveal";

const SpotifyIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.34c-.24.36-.66.48-1.02.24-2.82-1.74-6.36-2.1-10.56-1.14-.42.12-.78-.18-.9-.54-.12-.42.18-.78.54-.9 4.56-1.02 8.52-.6 11.64 1.32.42.18.48.66.3 1.02zm1.44-3.3c-.3.42-.84.6-1.26.3-3.24-1.98-8.16-2.58-11.94-1.38-.48.12-1.02-.12-1.14-.6-.12-.48.12-1.02.6-1.14 4.38-1.32 9.78-.66 13.5 1.62.36.18.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.3c-.6.18-1.2-.18-1.38-.72-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.72 1.62.54.3.72 1.02.42 1.56-.3.42-1.02.6-1.56.3z" />
  </svg>
);

export const Contact = () => {
  return (
    <footer
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-vt-gold/10 bg-vt-black px-6 pt-28 pb-12 md:px-16 md:pt-40 lg:px-24"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-8 flex items-center gap-4">
          <Overline>Contact & Management</Overline>
          <div className="vt-hairline flex-1" />
        </div>

        <h2 className="font-serif font-light leading-[0.95] tracking-tight text-vt-cream">
          <span className="block text-3xl sm:text-4xl md:text-5xl">
            <MaskLine>For bookings, press &</MaskLine>
          </span>
          <span className="block text-3xl italic text-vt-ash sm:text-4xl md:text-5xl">
            <MaskLine delay={0.06}>the quiet business of soul —</MaskLine>
          </span>
        </h2>

        <FadeUp delay={0.15}>
          <a
            data-testid="management-email-link"
            href={`mailto:${MANAGEMENT_EMAIL}`}
            className="group mt-10 inline-flex items-center gap-4 font-serif text-4xl italic tracking-tight text-vt-gold transition-colors duration-300 hover:text-vt-cream sm:text-5xl md:text-6xl lg:text-7xl"
          >
            {MANAGEMENT_EMAIL}
            <ArrowUpRight
              className="h-8 w-8 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 md:h-12 md:w-12"
              strokeWidth={1}
            />
          </a>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div className="mt-16 grid grid-cols-1 gap-10 border-t border-vt-gold/15 pt-12 md:grid-cols-3">
            <div>
              <Overline className="text-vt-muted">Management & Licensing</Overline>
              <a
                data-testid="press-email-link"
                href={`mailto:${MANAGEMENT_EMAIL}`}
                className="mt-3 block font-body text-base font-light tracking-wide text-vt-ash transition-colors duration-300 hover:text-vt-gold"
              >
                {MANAGEMENT_EMAIL}
              </a>
            </div>
            <div>
              <Overline className="text-vt-muted">The Label</Overline>
              <p className="mt-3 font-body text-base font-light leading-relaxed tracking-wide text-vt-ash">
                Old Lantern Records
                <br />
                An independent soul label
              </p>
            </div>
            <div>
              <Overline className="text-vt-muted">Follow The Universe</Overline>
              <div className="mt-4 flex gap-3">
                {[
                  ["spotify", SOCIALS.spotify, <SpotifyIcon key="s" size={18} />],
                  ["instagram", SOCIALS.instagram, <Instagram key="i" size={18} strokeWidth={1.4} />],
                  ["facebook", SOCIALS.facebook, <Facebook key="f" size={18} strokeWidth={1.4} />],
                ].map(([key, href, icon]) => (
                  <a
                    key={key}
                    data-testid={`social-${key}`}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={key}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-vt-gold/30 text-vt-ash transition-colors duration-300 hover:border-vt-gold hover:bg-vt-gold hover:text-vt-black"
                  >
                    {icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>

        <div className="mt-20 flex flex-col items-start justify-between gap-4 border-t border-vt-gold/10 pt-8 md:flex-row md:items-center">
          <div>
            <span className="font-serif text-lg tracking-tight text-vt-cream">The Velvet Testament</span>
            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.28em] text-vt-muted">
              An Old Lantern Records Universe
            </span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-vt-muted">
            © {new Date().getFullYear()} — Timeless Soul · Original Stories · Recurring Voices
          </p>
        </div>

        <p className="mt-6 max-w-2xl font-body text-xs font-light leading-relaxed tracking-wide text-vt-muted">
          The Velvet Testament is an original artistic universe of soul music. Its
          voices are characters, and its music is newly written and original.
        </p>
      </div>
    </footer>
  );
};
